import { getReportUniqueBusinessKey } from '../../../../../../../../../../utils/common';
import { buildReportsDiffTableData } from '../../../../reportDiff';
import type { BranchComparison } from '../../../../types';
import DescriptorCell from '../descriptorCell';
import { ReportHeader, ScrollTable } from '../../tableParts';
import type { ColumnLabels, GetTooltipContent } from '../../types';

type ReportsChangesPreviewProps = {
    comparison: BranchComparison;
    companyId?: string;
    columnLabels: ColumnLabels;
    getTooltipContent: GetTooltipContent;
};

function ReportsChangesPreview({ comparison, companyId, columnLabels, getTooltipContent }: ReportsChangesPreviewProps) {
    const { tableDiff, referenceBranch, targetBranch } = comparison;
    const { reports, differencesByReport } = buildReportsDiffTableData(
        referenceBranch.reports,
        targetBranch.reports,
        tableDiff.reportsDiff,
    );
    if (reports.length === 0) {
        return null;
    }

    const descriptors = tableDiff.reportsDiff.diffDescriptors.filter((descriptor) =>
        reports.some((report) => differencesByReport[getReportUniqueBusinessKey(report)]?.[descriptor.id]),
    );
    const reportsWithoutDescriptorRows = reports.filter((report) =>
        !descriptors.some((descriptor) => differencesByReport[getReportUniqueBusinessKey(report)]?.[descriptor.id]),
    );

    return (
        <>
            <ScrollTable className="branch-comparison-details__table--reports branch-comparison-details__table--preview">
                <thead>
                    <tr>
                        <th className="branch-comparison-details__column--descriptor">Descriptor</th>
                        {reports.map((report) => (
                            <ReportHeader
                                key={getReportUniqueBusinessKey(report)}
                                report={report}
                                label={getReportUniqueBusinessKey(report)}
                                companyId={companyId}
                            />
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {descriptors.map((descriptor) => (
                        <tr key={descriptor.id}>
                            <DescriptorCell
                                label={descriptor.label}
                                tooltipContent={getTooltipContent(descriptor.id, descriptor.label)}
                                descriptorId={descriptor.id}
                                columnLabels={columnLabels}
                            />
                            {reports.map((report) => {
                                const reportKey = getReportUniqueBusinessKey(report);
                                const changed = !!differencesByReport[reportKey]?.[descriptor.id];
                                return (
                                    <td key={reportKey}>
                                        {changed && (
                                            <span
                                                className="branch-comparison-details__preview-marker"
                                                role="img"
                                                aria-label="Changed in this report"
                                            />
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    {reportsWithoutDescriptorRows.length > 0 && (
                        <tr>
                            <td className="branch-comparison-details__column--descriptor">Report status</td>
                            {reports.map((report) => (
                                <td key={getReportUniqueBusinessKey(report)}>
                                    {reportsWithoutDescriptorRows.includes(report)
                                        ? `Report ${report.diffType.toLowerCase()}`
                                        : ''}
                                </td>
                            ))}
                        </tr>
                    )}
                </tbody>
            </ScrollTable>
        </>
    );
}

export default ReportsChangesPreview;
